import { useRef, useState, useEffect, useCallback } from 'react';
import { Text } from '~/components/atomic';
import useDeleteEmojiMutation from '~/features/emojis/useDeleteEmojiMutation';
import usePostEmojiMutation from '~/features/emojis/usePostEmojiMutation';
import { EmojiList, Emojis, Chat01RoomType } from '~/types';
import { completeEmojis, parseLikeNumber } from '~/utils';
import angry from '~/assets/img/emoji/angry.png';
import cool from '~/assets/img/emoji/cool.png';
import emojiDefault from '~/assets/img/emoji/default.png';
import like from '~/assets/img/emoji/like.png';
import money from '~/assets/img/emoji/money.png';
import plus from '~/assets/img/emoji/plus.png';
import surprise from '~/assets/img/emoji/surprise.png';
import tough from '~/assets/img/emoji/tough.png';
import * as S from './CharacterLike.styled';

export const EMOJI: { [key in EmojiList]: string } = {
  like,
  cool,
  surprise,
  tough,
  angry,
  money,
};

export interface CharacterLikeProps {
  emojis: Emojis[];
  character_id: string;
  type: Chat01RoomType;
}

export default function CharacterLike({
  emojis,
  character_id,
  type,
}: CharacterLikeProps) {
  const EMOJI_TYPES = ['like', 'cool', 'surprise', 'tough', 'angry', 'money'];

  const [emojiSelectOpen, setEmojiSelectOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const { mutate: postEmojiMutate } = usePostEmojiMutation();
  const { mutate: deleteEmojiMutate } = useDeleteEmojiMutation();

  const getModalEmojis = useCallback(() => {
    return completeEmojis(emojis);
  }, [emojis]);

  const modalEmojis = getModalEmojis();

  const handleEmojiOpenClick = () => {
    setEmojiSelectOpen((prev) => !prev);
  };

  const handleEmojiLikeClick = (is_clicked: boolean, emoji_id?: number) => {
    if (!emoji_id) return;

    const requestData =
      type === 'mission'
        ? { emoji_id, game_id: Number(character_id) }
        : { emoji_id, character_id };

    if (is_clicked) {
      deleteEmojiMutate(requestData);
    } else {
      postEmojiMutate(requestData);
    }
    setEmojiSelectOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setEmojiSelectOpen(false);
      }
    };

    if (emojiSelectOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [emojiSelectOpen]);

  return (
    <>
      <S.EmojiContainer ref={containerRef}>
        {emojis.map(({ emoji_id, emoji_type, count, is_clicked }) => {
          if (count) {
            return (
              <S.EmojiWrapper
                className='emoji_test'
                onClick={() => handleEmojiLikeClick(is_clicked, emoji_id)}
                key={emoji_id}
                active={is_clicked}
              >
                <img src={EMOJI[emoji_type]} alt={emoji_type} />
                <Text color='content_light_1' fontStyle='label.button14_medium'>
                  {parseLikeNumber(count)}
                </Text>
              </S.EmojiWrapper>
            );
          }
          return null;
        })}

        {emojis?.length !== 6 && (
          <S.EmojiOptionWrapper onClick={handleEmojiOpenClick}>
            <img src={emojiDefault} alt='like' />
            <img src={plus} alt='plus' />
          </S.EmojiOptionWrapper>
        )}

        {emojiSelectOpen && (
          <S.SelectModalContainer
            topHeight={containerRef.current?.clientHeight}
          >
            {modalEmojis.map(({ emoji_id, emoji_type, is_clicked }) => (
              <S.SelectOption
                key={emoji_id}
                onClick={() => handleEmojiLikeClick(is_clicked, emoji_id)}
              >
                <img src={EMOJI[emoji_type as EmojiList]} alt={emoji_type} />
                <Text fontStyle='label.list12_medium' color='content_light_3'>
                  {emoji_type}
                </Text>
              </S.SelectOption>
            ))}
          </S.SelectModalContainer>
        )}
      </S.EmojiContainer>
    </>
  );
}
